import { useState, useMemo, useEffect, Fragment } from 'react'
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { adminApi, type BookingStatus, type PaymentStatus, type DeliveryMethod } from '@/api/admin'
import { DeliveryMethodFields } from '@/components/shared/DeliveryMethodFields'
import { useTopBar } from '@/contexts/TopBarContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ChevronUp, ChevronDown, ChevronsUpDown, Search, X, UserCheck, AlertTriangle, Plus, FileText, Layers, Pencil, Trash2, GraduationCap, Check, Clock, Ban, Award } from 'lucide-react'
import { usePagination } from '@/hooks/usePagination'
import { TablePagination } from '@/components/shared/TablePagination'
import { ActionsMenu } from '@/components/shared/ActionsMenu'
import { CancelBookingModal } from '@/components/shared/CancelBookingModal'
import { VenueSelector } from '@/features/admin/venues/VenueSelector'
import { InvoiceBookingPicker } from '@/features/admin/invoices/InvoiceBookingPicker'
import EnquiriesTab from './EnquiriesTab'
import { NoEligibleTrainers } from '@/components/shared/NoEligibleTrainers'
import { TrainerAssignmentFields, type TrainerAssignment } from '@/components/shared/TrainerAssignmentFields'
import { useAdminDates } from '@/hooks/useDates'
import { formatTrainerConflict } from '@/lib/trainerConflicts'
import { bookingDaysFor, describeBookingDays } from '@/lib/duration'
import { useIssueProgress, IssueProgressBar } from './IssueProgress'

const BOOKING_STATUSES: BookingStatus[] = ['Booked', 'Completed', 'Cancelled', 'Rejected']

const statusLabel: Record<BookingStatus, string> = {
  Booked: 'Booked', Completed: 'Completed', Cancelled: 'Cancelled', Rejected: 'Rejected',
}
const statusColour: Record<BookingStatus, string> = {
  Booked:    'text-indigo-700 bg-indigo-50 border-indigo-200',
  Completed: 'text-green-700 bg-green-50 border-green-200',
  Cancelled: 'text-gray-500 bg-gray-100 border-gray-200',
  Rejected:  'text-amber-700 bg-amber-50 border-amber-200',
}

const paymentColour: Record<PaymentStatus, string> = {
  Unpaid: 'text-red-600',
  PartiallyPaid: 'text-yellow-600',
  Paid: 'text-green-600',
  Void: 'text-gray-400',
}
const paymentLabel: Record<PaymentStatus, string> = {
  Unpaid: 'Unpaid', PartiallyPaid: 'Part paid', Paid: 'Paid', Void: 'Void',
}

type SortKey = 'bookingDate' | 'clientName' | 'courseName' | 'places' | 'status' | 'invoicePaymentStatus'
type SortDir = 'asc' | 'desc'

function SortIcon({ col, sortKey, dir }: { col: SortKey; sortKey: SortKey; dir: SortDir }) {
  if (col !== sortKey) return <ChevronsUpDown size={13} className="text-gray-300 ml-1 inline" />
  return dir === 'asc'
    ? <ChevronUp size={13} className="text-indigo-500 ml-1 inline" />
    : <ChevronDown size={13} className="text-indigo-500 ml-1 inline" />
}

// ─── New Booking Modal ────────────────────────────────────────────────────────

const BOOKING_STATUS_OPTIONS: BookingStatus[] = ['Booked', 'Completed', 'Cancelled', 'Rejected']

function NewBookingModal({ onClose, onCreated }: {
  onClose: () => void
  onCreated: (bookingId: string) => void
}) {
  const queryClient = useQueryClient()

  const { data: clients = [] } = useQuery({
    queryKey: ['client-summaries'],
    queryFn: () => adminApi.getClientSummaries().then(r => r.data),
  })
  const { data: courses = [] } = useQuery({
    queryKey: ['courses'],
    queryFn: () => adminApi.getCourses().then(r => r.data),
  })
  const { data: trainers = [] } = useQuery({
    queryKey: ['trainers'],
    queryFn: () => adminApi.getTrainers().then(r => r.data),
  })

  const [clientSearch, setClientSearch] = useState('')
  const [clientId, setClientId] = useState<string | null>(null)
  const [courseId, setCourseId] = useState('')
  const [bookingDate, setBookingDate] = useState('')
  const [places, setPlaces] = useState(1)
  const [status, setStatus] = useState<BookingStatus>('Booked')
  const [venueId, setVenueId] = useState<string | null>(null)
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>('InPerson')
  const [meetingUrl, setMeetingUrl] = useState('')
  const [meetingInstructions, setMeetingInstructions] = useState('')
  const [notes, setNotes] = useState('')
  const [trainerAssignments, setTrainerAssignments] = useState<TrainerAssignment[]>([])

  const selectedClient = clients.find(c => c.id === clientId)
  const selectedCourse = courses.find(c => c.id === courseId)
  const daysNote = describeBookingDays(selectedCourse, bookingDate)

  const filteredClients = useMemo(() => {
    const q = clientSearch.toLowerCase().trim()
    return !q ? clients : clients.filter(c =>
      c.name.toLowerCase().includes(q) || (c.contactEmail ?? '').toLowerCase().includes(q)
    )
  }, [clients, clientSearch])

  const eligibleTrainers = trainers.filter(t =>
    t.isActive && (!courseId || t.qualifiedCourseIds.includes(courseId))
  )

  const assignedTrainerIds = bookingDate ? trainerAssignments.map(a => a.trainerId) : []
  const conflictQueries = useQueries({
    queries: assignedTrainerIds.map(tid => ({
      queryKey: ['trainer-conflicts', tid, bookingDate, undefined],
      queryFn: () => adminApi.getTrainerConflicts(tid, bookingDate).then(r => r.data),
      enabled: !!bookingDate,
    })),
  })
  const conflictMap: Record<string, string[]> = {}
  assignedTrainerIds.forEach((tid, i) => {
    const conflicts = conflictQueries[i].data ?? []
    if (conflicts.length > 0) conflictMap[tid] = conflicts.map(formatTrainerConflict)
  })

  const staffingWarning = trainerAssignments.length > 0 && !trainerAssignments.some(a => a.role === 'Lead')
    ? 'No lead trainer assigned.' : null

  const createMutation = useMutation({
    mutationFn: () => adminApi.createBooking(clientId!, {
      courseId,
      bookingDate,
      ...bookingDaysFor(selectedCourse, bookingDate),
      places,
      status,
      notes: notes.trim() || null,
      venueId: deliveryMethod === 'Online' ? null : venueId,
      attendeeIds: [],
      trainerAssignments,
      deliveryMethod,
      meetingUrl: deliveryMethod === 'Online' ? (meetingUrl.trim() || null) : null,
      meetingInstructions: deliveryMethod === 'Online' ? (meetingInstructions.trim() || null) : null,
    }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['all-bookings'] })
      onCreated(res.data.id)
    },
  })

  const canSubmit = !!clientId && !!courseId && !!bookingDate && places >= 1

  const inputCls = "h-10 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b shrink-0">
          <h2 className="text-base font-semibold text-gray-900">New client booking</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none cursor-pointer">×</button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
          {/* Client selector */}
          <div className="space-y-2">
            <Label>Client *</Label>
            {selectedClient ? (
              <div className="flex items-center justify-between rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2.5">
                <div>
                  <p className="text-sm font-medium text-indigo-900">{selectedClient.name}</p>
                  <p className="text-xs text-indigo-500">{selectedClient.contactEmail}</p>
                </div>
                <button
                  onClick={() => { setClientId(null); setClientSearch('') }}
                  className="text-indigo-400 hover:text-indigo-600 cursor-pointer ml-3"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <Input
                    value={clientSearch}
                    onChange={e => setClientSearch(e.target.value)}
                    placeholder="Search clients…"
                    className="pl-8"
                    autoFocus
                  />
                </div>
                <div className="max-h-44 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
                  {filteredClients.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-4">No clients found.</p>
                  ) : filteredClients.map(c => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setClientId(c.id)}
                      className="w-full text-left px-3 py-2.5 hover:bg-indigo-50 transition-colors cursor-pointer"
                    >
                      <p className="text-sm font-medium text-gray-800">{c.name}</p>
                      <p className="text-xs text-gray-400">{c.contactEmail}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Course + Date */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="nb-course">Course *</Label>
              <select
                id="nb-course"
                value={courseId}
                onChange={e => { setCourseId(e.target.value); setTrainerAssignments([]) }}
                className={inputCls}
              >
                <option value="">Select course…</option>
                {courses.map(c => (
                  <option key={c.id} value={c.id}>{c.code ? `${c.code} — ` : ''}{c.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="nb-date">Date *</Label>
              <Input id="nb-date" type="date" value={bookingDate} onChange={e => setBookingDate(e.target.value)} />
            </div>
          </div>

          {daysNote && <p className="text-xs text-gray-500 -mt-2">{daysNote}</p>}

          {/* Places + Status */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="nb-places">Places *</Label>
              <Input
                id="nb-places" type="number" min="1" value={places === 0 ? '' : places}
                onChange={e => { const v = e.target.value; setPlaces(v === '' ? 0 : parseInt(v) || 0) }}
                onBlur={() => setPlaces(p => Math.max(1, p))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="nb-status">Status</Label>
              <select id="nb-status" value={status} onChange={e => setStatus(e.target.value as BookingStatus)} className={inputCls}>
                {BOOKING_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Venue / delivery */}
          <DeliveryMethodFields
            value={{ deliveryMethod, meetingUrl, meetingInstructions }}
            onChange={next => { setDeliveryMethod(next.deliveryMethod); setMeetingUrl(next.meetingUrl); setMeetingInstructions(next.meetingInstructions) }}
            venueField={<VenueSelector id="nb-venue" value={venueId} onChange={setVenueId} />}
          />

          {/* Trainer assignment */}
          <div className="space-y-2">
            {eligibleTrainers.length === 0 ? (
              <>
                <Label>Trainer assignment</Label>
                <NoEligibleTrainers
                  hasCourse={!!courseId}
                  hasAnyTrainers={trainers.some(t => t.isActive)}
                />
              </>
            ) : (
              <TrainerAssignmentFields
                trainers={eligibleTrainers}
                assignments={trainerAssignments}
                onChange={setTrainerAssignments}
                conflictMap={conflictMap}
              />
            )}
            {staffingWarning && (
              <div className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-sm">
                <AlertTriangle size={14} className="shrink-0" />{staffingWarning}
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-1.5">
            <Label htmlFor="nb-notes">Notes</Label>
            <textarea
              id="nb-notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Optional"
              rows={3}
              className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t shrink-0">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => createMutation.mutate()} disabled={!canSubmit || createMutation.isPending}>
            {createMutation.isPending ? 'Creating…' : 'Create booking'}
          </Button>
        </div>
      </div>
    </div>
  )
}

function Th({ label, col, sortKey, dir, onSort }: {
  label: string; col: SortKey; sortKey: SortKey; dir: SortDir
  onSort: (col: SortKey) => void
}) {
  return (
    <th
      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700 whitespace-nowrap"
      onClick={() => onSort(col)}
    >
      {label}<SortIcon col={col} sortKey={sortKey} dir={dir} />
    </th>
  )
}

export default function BookingsListPage() {
  const { formatDate } = useAdminDates()
  const fmt = (date: string) => {
    return formatDate(date, { day: 'numeric', month: 'short', year: 'numeric' })
  }
  useTopBar(<span className="text-sm font-medium text-gray-900">Client Bookings</span>)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [mainTab, setMainTab] = useState<'bookings' | 'enquiries'>('bookings')
  const [showNewBooking, setShowNewBooking] = useState(false)

  const { data: bookings = [], isLoading } = useQuery({
    queryKey: ['all-bookings'],
    queryFn: () => adminApi.getAllBookings().then(r => r.data),
  })

  const deleteMutation = useMutation({
    mutationFn: adminApi.deleteBooking,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['all-bookings'] }),
    onError: () => alert('Cannot delete a booking that has an invoice.'),
  })

  const generateInvoiceMutation = useMutation({
    mutationFn: (b: { id: string; clientId: string }) => adminApi.createInvoice(b.clientId, { bookingIds: [b.id], dueDate: null, notes: null }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['all-bookings'] }),
    onError: () => alert('Failed to generate invoice.'),
  })

  // ── Bundle into invoice (picker scoped to the booking's client) ────────────
  const [bundleTarget, setBundleTarget] = useState<{ clientId: string; bookingId: string } | null>(null)
  const [bundleError, setBundleError] = useState<string | null>(null)
  const bundleInvoiceMutation = useMutation({
    mutationFn: (bookingIds: string[]) => adminApi.createInvoice(bundleTarget!.clientId, { bookingIds, dueDate: null, notes: null }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['all-bookings'] })
      setBundleTarget(null)
      navigate(`/admin/invoices/${res.data.id}`)
    },
    onError: () => setBundleError('Failed to create the invoice. Please try again.'),
  })

  const issueCertificatesMutation = useMutation({
    mutationFn: (bookingId: string) => adminApi.issueCertificates(bookingId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['all-bookings'] }),
    onError: () => alert('Failed to issue certificates.'),
  })

  // ── Cancel booking ──────────────────────────────────────────────────────────
  const [cancelTarget, setCancelTarget] = useState<{ id: string; courseName: string; clientName: string } | null>(null)
  const cancelMutation = useMutation({
    mutationFn: ({ id, notifyClient }: { id: string; notifyClient: boolean }) =>
      adminApi.cancelBooking(id, notifyClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-bookings'] })
      setCancelTarget(null)
    },
    onError: () => alert('Failed to cancel booking — it may already be completed or invoiced.'),
  })

  // ── Inline "complete booking" ───────────────────────────────────────────────
  const [completingBookingId, setCompletingBookingId] = useState<string | null>(null)
  const [passingAttendeeIds, setPassingAttendeeIds] = useState<string[]>([])

  const { data: completingBooking } = useQuery({
    queryKey: ['booking-detail-for-complete', completingBookingId],
    queryFn: () => adminApi.getBooking(completingBookingId!).then(r => r.data),
    enabled: !!completingBookingId,
  })

  useEffect(() => {
    if (completingBooking) setPassingAttendeeIds(completingBooking.attendees.map(a => a.staffMemberId))
  }, [completingBooking])

  const openCompletion = (bookingId: string) => {
    setCompletingBookingId(bookingId)
    setPassingAttendeeIds([])
  }

  const completeMutation = useMutation({
    mutationFn: async ({ bookingId, ids }: { bookingId: string; ids: string[] }) => {
      await adminApi.completeBooking(bookingId, ids)
      return adminApi.issueCertificates(bookingId)
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['all-bookings'] })
      setCompletingBookingId(null)
      const { trainingRecordsCreated: created, alreadyExisted: skipped, certificatesGenerated: certs } = res.data
      const parts = []
      if (created > 0) parts.push(`${created} training record${created === 1 ? '' : 's'} created`)
      if (skipped > 0) parts.push(`${skipped} skipped (record already existed for this course)`)
      if (certs > 0) parts.push(`${certs} certificate${certs === 1 ? '' : 's'} generated`)
      if (parts.length === 0) parts.push('no new records created')
      alert(`Booking marked complete. ${parts.join(', ')}.`)
    },
    onError: () => alert('Failed to complete booking.'),
  })

  const completeProgress = useIssueProgress(completingBookingId, completeMutation.isPending)

  const { data: enquiries = [] } = useQuery({
    queryKey: ['booking-enquiries'],
    queryFn: () => adminApi.getEnquiries().then(r => r.data),
  })
  const pendingCount = enquiries.filter(e => e.status === 'Pending' || e.status === 'Acknowledged').length

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<BookingStatus | ''>('')
  const [sortKey, setSortKey] = useState<SortKey>('bookingDate')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  const handleSort = (col: SortKey) => {
    if (sortKey === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(col); setSortDir('desc') }
  }

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim()
    return bookings
      .filter(b => !statusFilter || b.status === statusFilter)
      .filter(b => !q || [b.clientName, b.courseName, b.courseCode ?? '', b.invoiceNumber ?? '']
        .some(v => v.toLowerCase().includes(q)))
      .sort((a, b) => {
        let cmp = 0
        switch (sortKey) {
          case 'bookingDate':          cmp = a.bookingDate.localeCompare(b.bookingDate); break
          case 'clientName':           cmp = a.clientName.localeCompare(b.clientName); break
          case 'courseName':           cmp = a.courseName.localeCompare(b.courseName); break
          case 'places':               cmp = a.places - b.places; break
          case 'status':               cmp = a.status.localeCompare(b.status); break
          case 'invoicePaymentStatus': cmp = (a.invoicePaymentStatus ?? '').localeCompare(b.invoicePaymentStatus ?? ''); break
        }
        return sortDir === 'asc' ? cmp : -cmp
      })
  }, [bookings, search, statusFilter, sortKey, sortDir])

  const { paged, page, setPage, totalPages, total, pageSize, start } = usePagination(filtered)

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-6">
        <p className="text-sm text-gray-500">Training booked for a specific client.</p>
        <div className="flex items-center gap-3">
          {mainTab === 'bookings' && (
            <div className="text-sm text-gray-400">{filtered.length} of {bookings.length}</div>
          )}
          <Button size="sm" onClick={() => setShowNewBooking(true)}>
            <Plus size={15} className="mr-1.5" /> New client booking
          </Button>
        </div>
      </div>

      {showNewBooking && (
        <NewBookingModal
          onClose={() => setShowNewBooking(false)}
          onCreated={(id) => { setShowNewBooking(false); navigate(`/admin/bookings/${id}`) }}
        />
      )}

      {/* Main tab switcher */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit mb-6">
        <button
          onClick={() => setMainTab('bookings')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer ${
            mainTab === 'bookings' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Bookings
        </button>

        <button
          onClick={() => setMainTab('enquiries')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2 cursor-pointer ${
            mainTab === 'enquiries' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Enquiries
          {pendingCount > 0 && (
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-500 text-white text-xs font-bold">
              {pendingCount}
            </span>
          )}
        </button>
      </div>

      {mainTab === 'enquiries' && <EnquiriesTab />}

      {mainTab === 'bookings' && <>
      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        {/* Search */}
        <div className="relative flex-1 min-w-56">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search client, course, invoice #…"
            className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer">
              <X size={14} />
            </button>
          )}
        </div>

        {/* Status filter pills */}
        <div className="flex gap-1.5 flex-wrap">
          <button
            onClick={() => setStatusFilter('')}
            className={`px-3 py-1.5 text-xs font-medium rounded-full border cursor-pointer transition-colors ${
              statusFilter === '' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
            }`}
          >
            All
          </button>
          {BOOKING_STATUSES.map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(statusFilter === s ? '' : s)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full border cursor-pointer transition-colors ${
                statusFilter === s ? statusColour[s] + ' font-semibold' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
              }`}
            >
              {statusLabel[s]}
            </button>
          ))}
        </div>
      </div>

      {/* Mobile card list */}
      <div className="sm:hidden space-y-2">
        {isLoading && <p className="text-center text-gray-500 py-8">Loading…</p>}
        {!isLoading && filtered.length === 0 && (
          <p className="text-center text-gray-400 py-8">
            {bookings.length === 0 ? 'No bookings yet.' : 'No bookings match your filters.'}
          </p>
        )}
        {paged.map(b => (
          <div
            key={b.id}
            onClick={() => navigate(`/admin/bookings/${b.id}`)}
            className="bg-white border border-gray-200 rounded-lg p-4 cursor-pointer hover:border-indigo-200"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="font-medium text-gray-900 truncate">{b.courseName}</p>
                <p className="text-sm text-gray-500 truncate">{b.clientName}</p>
              </div>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border shrink-0 ${statusColour[b.status]}`}>
                {statusLabel[b.status]}
              </span>
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
              <span>
                {fmt(b.bookingDate)}
                {b.additionalDaysCount > 0 && <span className="ml-1 text-gray-400">+{b.additionalDaysCount}d</span>}
              </span>
              <span>{b.places} places</span>
              {b.leadTrainerName ? (
                <span>{b.leadTrainerName}</span>
              ) : b.additionalTrainerCount > 0 ? (
                <span className="text-amber-700">No lead trainer</span>
              ) : (
                <span className="text-gray-400">Trainer TBC</span>
              )}
              {b.invoicePaymentStatus && (
                <span className={paymentColour[b.invoicePaymentStatus]}>{paymentLabel[b.invoicePaymentStatus]}</span>
              )}
            </div>
          </div>
        ))}
        <TablePagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} start={start} onPage={setPage} />
      </div>

      {/* Desktop table */}
      <div className="hidden sm:block bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <Th label="Date"       col="bookingDate"          sortKey={sortKey} dir={sortDir} onSort={handleSort} />
                <Th label="Client"     col="clientName"           sortKey={sortKey} dir={sortDir} onSort={handleSort} />
                <Th label="Course"     col="courseName"           sortKey={sortKey} dir={sortDir} onSort={handleSort} />
                <Th label="Attendees"  col="places"               sortKey={sortKey} dir={sortDir} onSort={handleSort} />
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Lead trainer</th>
                <Th label="Status"     col="status"               sortKey={sortKey} dir={sortDir} onSort={handleSort} />
                <Th label="Invoice"    col="invoicePaymentStatus" sortKey={sortKey} dir={sortDir} onSort={handleSort} />
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading && (
                <tr><td colSpan={8} className="text-center text-gray-500 py-12">Loading…</td></tr>
              )}
              {!isLoading && filtered.length === 0 && (
                <tr><td colSpan={8} className="text-center text-gray-400 py-12">
                  {bookings.length === 0 ? 'No bookings yet.' : 'No bookings match your filters.'}
                </td></tr>
              )}
              {paged.map(b => {
                const hasAnyTrainer = !!b.leadTrainerName || b.additionalTrainerCount > 0
                const hasTrainerWarning = !b.leadTrainerName && hasAnyTrainer
                const attendeeMismatch = b.attendeeCount > 0 && b.attendeeCount !== b.places
                const isCompleting = completingBookingId === b.id

                return (
                <Fragment key={b.id}>
                <tr
                  className={`hover:bg-indigo-50/40 cursor-pointer transition-colors ${isCompleting ? 'bg-indigo-50/40' : ''}`}
                  onClick={() => navigate(`/admin/bookings/${b.id}`)}
                >
                  <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                    {fmt(b.bookingDate)}
                    {b.additionalDaysCount > 0 && (
                      <span className="ml-1.5 text-xs text-gray-400">+{b.additionalDaysCount}d</span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {b.clientName}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {b.courseName}
                    {b.courseCode && <span className="ml-1.5 text-xs text-gray-400 font-mono">{b.courseCode}</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    <span className="inline-flex items-center gap-1.5">
                      {b.places}
                      {attendeeMismatch ? (
                        <span
                          className="inline-flex items-center gap-0.5 text-xs text-amber-600 font-medium"
                          title={b.attendeeCount > b.places
                            ? `${b.attendeeCount} attendees assigned but only ${b.places} places booked`
                            : `${b.places} places booked but only ${b.attendeeCount} attendees assigned`}
                        >
                          <AlertTriangle size={12} />
                          {b.attendeeCount}
                        </span>
                      ) : b.attendeeCount > 0 ? (
                        <span className="text-xs text-gray-400">({b.attendeeCount})</span>
                      ) : null}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {hasTrainerWarning ? (
                      <span className="flex items-center gap-1 text-amber-700" title="No lead trainer assigned">
                        <AlertTriangle size={13} className="shrink-0" />
                        No lead trainer
                      </span>
                    ) : b.leadTrainerName ? (
                      <span className="flex items-center gap-1 text-gray-600">
                        <UserCheck size={13} className="text-gray-400 shrink-0" />
                        {b.leadTrainerName}
                        {b.additionalTrainerCount > 0 && (
                          <span className="text-xs text-gray-400">+{b.additionalTrainerCount}</span>
                        )}
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-gray-400" title="No trainer assigned yet">
                        <Clock size={13} className="shrink-0" />
                        Trainer TBC
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-0.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${statusColour[b.status]}`}>
                        {statusLabel[b.status]}
                      </span>
                      {b.status === 'Rejected' && b.rejectionReason && (
                        <span className="text-xs text-amber-700 truncate max-w-[160px]" title={b.rejectionReason}>
                          "{b.rejectionReason}"
                        </span>
                      )}
                      {b.status === 'Completed' && !b.certificatesIssuedAt && (
                        <span className="text-xs text-indigo-600">Certs pending</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {b.invoiceId ? (
                      <Link
                        to={`/admin/invoices/${b.invoiceId}`}
                        className="font-mono text-xs text-indigo-600 hover:underline"
                        onClick={e => e.stopPropagation()}
                      >
                        {b.invoiceNumber}
                      </Link>
                    ) : (
                      <span className="text-gray-300 text-xs">—</span>
                    )}
                    {b.invoicePaymentStatus && (
                      <span className={`ml-2 text-xs font-medium ${paymentColour[b.invoicePaymentStatus]}`}>
                        {paymentLabel[b.invoicePaymentStatus]}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                    <ActionsMenu
                      items={[
                        ...(b.status === 'Booked' ? [{
                          label: isCompleting ? 'Cancel completing' : 'Complete booking',
                          icon: <GraduationCap size={14} className="text-green-600" />,
                          onClick: () => isCompleting ? setCompletingBookingId(null) : openCompletion(b.id),
                        }] : []),
                        ...(b.status === 'Completed' && !b.certificatesIssuedAt ? [{
                          label: 'Issue certificates',
                          icon: <Award size={14} className="text-indigo-600" />,
                          onClick: () => issueCertificatesMutation.mutate(b.id),
                          disabled: issueCertificatesMutation.isPending,
                        }] : []),
                        ...(!b.invoiceId && (b.status === 'Completed' || b.status === 'Booked') ? [{
                          label: 'Generate invoice',
                          icon: <FileText size={14} className="text-gray-500" />,
                          onClick: () => generateInvoiceMutation.mutate({ id: b.id, clientId: b.clientId }),
                          disabled: generateInvoiceMutation.isPending,
                        }] : []),
                        ...(!b.invoiceId && (b.status === 'Completed' || b.status === 'Booked') ? [{
                          label: 'Bundle into invoice…',
                          icon: <Layers size={14} className="text-gray-500" />,
                          onClick: () => { setBundleError(null); setBundleTarget({ clientId: b.clientId, bookingId: b.id }) },
                        }] : []),
                        {
                          label: 'Edit booking',
                          icon: <Pencil size={14} className="text-gray-500" />,
                          onClick: () => navigate(`/admin/bookings/${b.id}`),
                        },
                        ...(b.status === 'Booked' && !b.invoiceId ? [{
                          label: 'Cancel booking',
                          icon: <Ban size={14} className="text-amber-600" />,
                          onClick: () => setCancelTarget({ id: b.id, courseName: b.courseName, clientName: b.clientName }),
                        }] : []),
                        ...(b.status === 'Cancelled' ? [{
                          label: 'Delete booking',
                          icon: <Trash2 size={14} className="text-red-600" />,
                          onClick: () => {
                            if (confirm(`Delete booking for "${b.courseName}" (${b.clientName})?`))
                              deleteMutation.mutate(b.id)
                          },
                          disabled: deleteMutation.isPending,
                          danger: true,
                        }] : []),
                      ]}
                    />
                  </td>
                </tr>
                {isCompleting && (
                  <tr>
                    <td colSpan={8} className="px-4 py-4 bg-indigo-50/40 border-t border-indigo-100">
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900">Complete booking — select who passed</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            A training record will be created for each ticked attendee. Untick anyone who did not pass or attend.
                          </p>
                        </div>
                        {!completingBooking || completingBooking.id !== b.id ? (
                          <p className="text-sm text-gray-400">Loading attendees…</p>
                        ) : completingBooking.attendees.length === 0 ? (
                          <p className="text-sm text-gray-400">No attendees assigned to this booking yet.</p>
                        ) : (
                          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 lg:grid-cols-4">
                            {completingBooking.attendees.map(a => {
                              const selected = passingAttendeeIds.includes(a.staffMemberId)
                              return (
                                <button
                                  key={a.staffMemberId}
                                  type="button"
                                  onClick={() => setPassingAttendeeIds(ids =>
                                    selected ? ids.filter(x => x !== a.staffMemberId) : [...ids, a.staffMemberId]
                                  )}
                                  className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm text-left transition-colors cursor-pointer ${
                                    selected
                                      ? 'border-green-400 bg-green-50 text-green-800'
                                      : 'border-gray-200 bg-white text-gray-400 line-through'
                                  }`}
                                >
                                  {selected && <Check size={13} className="shrink-0 text-green-600" />}
                                  <span className="truncate">{a.firstName} {a.lastName}</span>
                                </button>
                              )
                            })}
                          </div>
                        )}
                        {completeMutation.isPending && <IssueProgressBar progress={completeProgress} />}
                        <div className="flex gap-2 pt-1">
                          <Button
                            size="sm"
                            className="gap-1.5"
                            disabled={completeMutation.isPending || !completingBooking || completingBooking.id !== b.id}
                            onClick={() => completeMutation.mutate({ bookingId: b.id, ids: passingAttendeeIds })}
                          >
                            <GraduationCap size={14} />
                            {completeMutation.isPending
                              ? 'Completing…'
                              : `Complete & create ${passingAttendeeIds.length} record${passingAttendeeIds.length === 1 ? '' : 's'}`}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setCompletingBookingId(null)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
        <TablePagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} start={start} onPage={setPage} />
      </div>
      </>}

      {cancelTarget && (
        <CancelBookingModal
          booking={cancelTarget}
          onClose={() => setCancelTarget(null)}
          onConfirm={notifyClient => cancelMutation.mutate({ id: cancelTarget.id, notifyClient })}
          isPending={cancelMutation.isPending}
        />
      )}

      {bundleTarget && (
        <InvoiceBookingPicker
          clientId={bundleTarget.clientId}
          initiallySelected={[bundleTarget.bookingId]}
          title="Create invoice"
          confirmLabel="Create invoice"
          pending={bundleInvoiceMutation.isPending}
          error={bundleError}
          onConfirm={bookingIds => bundleInvoiceMutation.mutate(bookingIds)}
          onClose={() => setBundleTarget(null)}
        />
      )}
    </div>
  )
}
